/**
 * Sistema centralizado de permisos para CAPIVIO
 * Define roles y permisos específicos para cada tipo de administración
 */

// Definición de roles del sistema (compatible con ambos formatos)
export const ROLES = {
    SUPER_ADMIN: "SUPER_ADMIN", // Formato principal
    super_admin: "super_admin", // Formato alternativo para compatibilidad
    BUSINESS_ADMIN: "business_admin", 
    SYSTEM_ADMIN: "system_admin",
    LEGAL_ADMIN: "legal_admin",
    REVIEWER: "reviewer",
    DEVOPS: "devops",
    AGENT: "agent",
    USER: "user"
};

// Definición de permisos específicos
export const PERMISSIONS = {
    // Permisos de Negocio
    BUSINESS: {
        MANAGE_USERS: "business:manage_users",
        MANAGE_AGENTS: "business:manage_agents",
        MANAGE_OPPORTUNITIES: "business:manage_opportunities",
        MANAGE_KYC: "business:manage_kyc",
        MANAGE_OFFBOARDING: "business:manage_offboarding",
        VIEW_BUSINESS_REPORTS: "business:view_reports",
        MANAGE_CAPITAL: "business:manage_capital",
        MANAGE_COMMISSIONS: "business:manage_commissions",
        AUDIT_USER_ACTIONS: "business:audit_users"
    },
    
    // Permisos de Sistema
    SYSTEM: {
        MANAGE_SERIALIZATION: "system:manage_serialization",
        VIEW_TRACEABILITY: "system:view_traceability",
        MANAGE_MIGRATIONS: "system:manage_migrations",
        VIEW_SYSTEM_LOGS: "system:view_logs",
        MANAGE_BACKUPS: "system:manage_backups",
        CONFIGURE_SYSTEM: "system:configure",
        VIEW_PERFORMANCE: "system:view_performance",
        MANAGE_ALERTS: "system:manage_alerts",
        ACCESS_DEV_TOOLS: "system:dev_tools"
    },
    
    // Permisos de Auditoría
    AUDIT: {
        VIEW_AUDIT_LOGS: "audit:view_logs",
        EXPORT_AUDIT_DATA: "audit:export_data",
        VIEW_COMPLIANCE_REPORTS: "audit:compliance_reports",
        MANAGE_AUDIT_CONFIG: "audit:manage_config"
    },
    
    // Permisos de Exportación
    EXPORT: {
        EXPORT_USER_DATA: "export:user_data",
        EXPORT_OPPORTUNITY_DATA: "export:opportunity_data",
        EXPORT_AUDIT_LOGS: "export:audit_logs",
        EXPORT_SERIALIZATION_DATA: "export:serialization_data"
    }
};

// Mapeo de roles a permisos (compatible con ambos formatos)
export const ROLE_PERMISSIONS = {
    [ROLES.SUPER_ADMIN]: [
        // Todos los permisos
        ...Object.values(PERMISSIONS.BUSINESS),
        ...Object.values(PERMISSIONS.SYSTEM),
        ...Object.values(PERMISSIONS.AUDIT),
        ...Object.values(PERMISSIONS.EXPORT)
    ],
    // Alias para compatibilidad
    [ROLES.super_admin]: [
        // Todos los permisos
        ...Object.values(PERMISSIONS.BUSINESS),
        ...Object.values(PERMISSIONS.SYSTEM),
        ...Object.values(PERMISSIONS.AUDIT),
        ...Object.values(PERMISSIONS.EXPORT)
    ],
    
    [ROLES.BUSINESS_ADMIN]: [
        // Permisos de negocio completos
        ...Object.values(PERMISSIONS.BUSINESS),
        // Algunos permisos de auditoría
        PERMISSIONS.AUDIT.VIEW_AUDIT_LOGS,
        PERMISSIONS.AUDIT.VIEW_COMPLIANCE_REPORTS,
        // Permisos de exportación limitados
        PERMISSIONS.EXPORT.EXPORT_USER_DATA,
        PERMISSIONS.EXPORT.EXPORT_OPPORTUNITY_DATA
    ],
    
    [ROLES.SYSTEM_ADMIN]: [
        // Permisos de sistema completos
        ...Object.values(PERMISSIONS.SYSTEM),
        // Algunos permisos de auditoría
        PERMISSIONS.AUDIT.VIEW_AUDIT_LOGS,
        PERMISSIONS.AUDIT.EXPORT_AUDIT_DATA,
        // Permisos de exportación técnicos
        PERMISSIONS.EXPORT.EXPORT_SERIALIZATION_DATA
    ],
    
    // Agregar rol ADMIN con permisos completos
    "ADMIN": [
        // Todos los permisos (igual que SUPER_ADMIN)
        ...Object.values(PERMISSIONS.BUSINESS),
        ...Object.values(PERMISSIONS.SYSTEM),
        ...Object.values(PERMISSIONS.AUDIT),
        ...Object.values(PERMISSIONS.EXPORT)
    ],
    
    // Alias para compatibilidad con normalización
    "admin": [
        // Todos los permisos (igual que SUPER_ADMIN)
        ...Object.values(PERMISSIONS.BUSINESS),
        ...Object.values(PERMISSIONS.SYSTEM),
        ...Object.values(PERMISSIONS.AUDIT),
        ...Object.values(PERMISSIONS.EXPORT)
    ],
    
    [ROLES.LEGAL_ADMIN]: [
        // Permisos de compliance y legal
        PERMISSIONS.BUSINESS.MANAGE_KYC,
        PERMISSIONS.BUSINESS.MANAGE_OFFBOARDING,
        PERMISSIONS.BUSINESS.AUDIT_USER_ACTIONS,
        PERMISSIONS.AUDIT.VIEW_AUDIT_LOGS,
        PERMISSIONS.AUDIT.VIEW_COMPLIANCE_REPORTS,
        PERMISSIONS.AUDIT.EXPORT_AUDIT_DATA,
        PERMISSIONS.EXPORT.EXPORT_USER_DATA
    ],
    
    [ROLES.REVIEWER]: [
        // Permisos de revisión
        PERMISSIONS.BUSINESS.MANAGE_OPPORTUNITIES,
        PERMISSIONS.BUSINESS.VIEW_BUSINESS_REPORTS,
        PERMISSIONS.AUDIT.VIEW_AUDIT_LOGS,
        PERMISSIONS.EXPORT.EXPORT_OPPORTUNITY_DATA
    ],
    
    [ROLES.DEVOPS]: [
        // Permisos técnicos limitados
        PERMISSIONS.SYSTEM.VIEW_SYSTEM_LOGS,
        PERMISSIONS.SYSTEM.VIEW_PERFORMANCE,
        PERMISSIONS.SYSTEM.MANAGE_ALERTS,
        PERMISSIONS.SYSTEM.ACCESS_DEV_TOOLS
    ]
};

/**
 * Normaliza el rol del usuario para compatibilidad
 * @param {string} role - Rol del usuario
 * @returns {string} Rol normalizado
 */
const normalizeRole = (role) => {
    if (!role) return null;
    
    // Mapeo de compatibilidad
    const roleMap = {
        "SUPER_ADMIN": ROLES.SUPER_ADMIN,
        "super_admin": ROLES.SUPER_ADMIN,
        "ADMIN": "admin", // Mapear ADMIN a admin para compatibilidad
        "admin": "admin"
    };
    
    return roleMap[role] || role;
};

/**
 * Verifica si un usuario tiene un permiso específico
 * @param {Object} user - Objeto usuario con role
 * @param {string} permission - Permiso a verificar
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
    if (!user || !user.role) return false;
    
    const normalizedRole = normalizeRole(user.role);
    const userPermissions = ROLE_PERMISSIONS[normalizedRole] || [];
    return userPermissions.includes(permission);
};

/**
 * Verifica si un usuario tiene acceso a admin de negocio
 * @param {Object} user - Objeto usuario
 * @returns {boolean}
 */
export const hasBusinessAccess = (user) => {
    if (!user || !user.role) return false;
    // Permitir acceso directo a SUPER_ADMIN
    if (normalizeRole(user.role) === ROLES.SUPER_ADMIN) return true;
    return hasPermission(user, PERMISSIONS.BUSINESS.MANAGE_USERS) ||
           hasPermission(user, PERMISSIONS.BUSINESS.MANAGE_OPPORTUNITIES) ||
           hasPermission(user, PERMISSIONS.BUSINESS.MANAGE_KYC);
};

/**
 * Verifica si un usuario tiene acceso a admin de sistema
 * @param {Object} user - Objeto usuario
 * @returns {boolean}
 */
export const hasSystemAccess = (user) => {
    if (!user || !user.role) return false;
    // Permitir acceso directo a SUPER_ADMIN
    if (normalizeRole(user.role) === ROLES.SUPER_ADMIN) return true;
    return hasPermission(user, PERMISSIONS.SYSTEM.MANAGE_SERIALIZATION) ||
           hasPermission(user, PERMISSIONS.SYSTEM.VIEW_SYSTEM_LOGS) ||
           hasPermission(user, PERMISSIONS.SYSTEM.CONFIGURE_SYSTEM);
};

/**
 * Verifica si un usuario tiene acceso a auditoría
 * @param {Object} user - Objeto usuario
 * @returns {boolean}
 */
export const hasAuditAccess = (user) => {
    return hasPermission(user, PERMISSIONS.AUDIT.VIEW_AUDIT_LOGS) ||
           hasPermission(user, PERMISSIONS.AUDIT.EXPORT_AUDIT_DATA);
};

/**
 * Obtiene todos los permisos de un usuario
 * @param {Object} user - Objeto usuario
 * @returns {Array} Lista de permisos
 */
export const getUserPermissions = (user) => {
    if (!user || !user.role) return [];
    const normalizedRole = normalizeRole(user.role);
    return ROLE_PERMISSIONS[normalizedRole] || [];
};

/**
 * Verifica si un usuario puede exportar datos
 * @param {Object} user - Objeto usuario
 * @param {string} dataType - Tipo de datos a exportar
 * @returns {boolean}
 */
export const canExportData = (user, dataType) => {
    switch (dataType) {
    case "user_data":
        return hasPermission(user, PERMISSIONS.EXPORT.EXPORT_USER_DATA);
    case "opportunity_data":
        return hasPermission(user, PERMISSIONS.EXPORT.EXPORT_OPPORTUNITY_DATA);
    case "audit_logs":
        return hasPermission(user, PERMISSIONS.EXPORT.EXPORT_AUDIT_LOGS);
    case "serialization_data":
        return hasPermission(user, PERMISSIONS.EXPORT.EXPORT_SERIALIZATION_DATA);
    default:
        return false;
    }
};

/**
 * Verifica si un usuario puede gestionar serialización
 * @param {Object} user - Objeto usuario
 * @returns {boolean}
 */
export const canManageSerialization = (user) => {
    return hasPermission(user, PERMISSIONS.SYSTEM.MANAGE_SERIALIZATION);
};

/**
 * Verifica si un usuario puede ver trazabilidad
 * @param {Object} user - Objeto usuario
 * @returns {boolean}
 */
export const canViewTraceability = (user) => {
    return hasPermission(user, PERMISSIONS.SYSTEM.VIEW_TRACEABILITY);
};

/**
 * Verifica si un usuario puede gestionar migraciones
 * @param {Object} user - Objeto usuario
 * @returns {boolean}
 */
export const canManageMigrations = (user) => {
    return hasPermission(user, PERMISSIONS.SYSTEM.MANAGE_MIGRATIONS);
};

/**
 * Obtiene el nivel de acceso del usuario
 * @param {Object} user - Objeto usuario
 * @returns {string} Nivel de acceso
 */
export const getUserAccessLevel = (user) => {
    if (!user || !user.role) return "none";
    
    const normalizedRole = normalizeRole(user.role);
    if (normalizedRole === ROLES.SUPER_ADMIN) return "super";
    if (hasSystemAccess(user)) return "system";
    if (hasBusinessAccess(user)) return "business";
    if (hasAuditAccess(user)) return "audit";
    
    return "limited";
};

/**
 * Hook personalizado para permisos (para usar en componentes)
 * @param {Object} user - Objeto usuario
 * @returns {Object} Funciones de verificación de permisos
 */
export const usePermissions = (user) => {
    return {
        hasPermission: (permission) => hasPermission(user, permission),
        hasBusinessAccess: () => hasBusinessAccess(user),
        hasSystemAccess: () => hasSystemAccess(user),
        hasAuditAccess: () => hasAuditAccess(user),
        canExportData: (dataType) => canExportData(user, dataType),
        canManageSerialization: () => canManageSerialization(user),
        canViewTraceability: () => canViewTraceability(user),
        canManageMigrations: () => canManageMigrations(user),
        getUserAccessLevel: () => getUserAccessLevel(user),
        getUserPermissions: () => getUserPermissions(user)
    };
}; 